import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import {
  masterAccentButton,
  masterActiveColor,
  masterCancelButton,
  masterEditButton,
  masterFormActions,
  masterFormContainer,
  masterFormGrid3,
  masterFormGridRow,
  masterInactiveColor,
  masterInput,
  masterInputLabel,
  masterPageStack,
  masterPanel,
  masterPrimaryButton,
  masterSectionTitle,
  masterSelect,
  masterStickyHeadRow,
  masterTable,
  masterTableActionCell,
  masterTableData,
  masterTableHeaderCell,
  masterTableHeaderCellCenter,
  masterTableRow,
  masterTableStatusCell,
  masterTableWrap,
  masterTopActionBar
} from '../Pages/MasterCssClass';

const emptyForm = {
  bankName: '',
  accountNumber: '',
  ifscCode: '',
  holderName: '',
  accountType: '',
  branchName: '',
  status: 'Active'
};

const CompanyBankingDetails = () => {
  const [bankingDetails, setBankingDetails] = useState([]);
  const [showList, setShowList] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchBankingDetails = async () => {
    try {
      const res = await api.get('/company-banking-details');
      setBankingDetails(res.data);
    } catch (err) {
      console.error('Error fetching company banking details', err);
    }
  };

  useEffect(() => {
    fetchBankingDetails();
  }, []);

  const handleChange = (field, value) => {
    if (field === 'accountNumber') {
      // Allow only numbers
      const numericValue = value.replace(/\D/g, '');
      setFormData((current) => ({ ...current, [field]: numericValue }));
    } else {
      setFormData((current) => ({ ...current, [field]: value }));
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editId) {
        await api.put(`/company-banking-details/${editId}`, formData);
        toast.success('Company banking details updated successfully!');
      } else {
        const res = await api.post('/company-banking-details', formData);
        toast.success(`Company banking details saved successfully. ID: ${res.data.bankingDetailsId}`);
      }

      resetForm();
      fetchBankingDetails();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      toast.error(`Error saving company banking details: ${errorMsg}`);
    }
  };

  const handleEdit = (details) => {
    setFormData({
      bankName: details.bankName || '',
      accountNumber: details.accountNumber || '',
      ifscCode: details.ifscCode || '',
      holderName: details.holderName || '',
      accountType: details.accountType || '',
      branchName: details.branchName || '',
      status: details.status || 'Active'
    });
    setEditId(details._id);
    setShowList(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete these banking details?')) return;
    try {
      await api.delete(`/company-banking-details/${id}`);
      toast.success('Banking details deleted successfully');
      fetchBankingDetails();
    } catch (err) {
      toast.error('Failed to delete banking details');
    }
  };

  return (
    <div className={masterPageStack}>
      <div className={masterTopActionBar}>
        <button
          onClick={() => setShowList(!showList)}
          className={masterPrimaryButton}
        >
          {showList ? 'Add Banking Details' : `View List (${bankingDetails.length})`}
        </button>
      </div>

      {!showList ? (
        <div className={masterPanel}>
          <div className={masterFormContainer}>
            <h2 className={masterSectionTitle}>
              {editId ? 'Edit Company Banking Details' : 'Company Banking Details Entry'}
            </h2>

            <form onSubmit={handleSubmit} className={masterPageStack}>
              <div className={masterFormGrid3}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Bank Name</label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => handleChange('bankName', e.target.value)}
                    className={masterInput}
                    required
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Account Number</label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => handleChange('accountNumber', e.target.value)}
                    className={masterInput}
                    required
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>IFSC Code</label>
                  <input
                    type="text"
                    value={formData.ifscCode}
                    onChange={(e) => handleChange('ifscCode', e.target.value)}
                    className={masterInput}
                    required
                  />
                </div>
              </div>

              <div className={masterFormGrid3}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Holder Name</label>
                  <input
                    type="text"
                    value={formData.holderName}
                    onChange={(e) => handleChange('holderName', e.target.value)}
                    className={masterInput}
                    required
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Account Type</label>
                  <select
                    value={formData.accountType}
                    onChange={(e) => handleChange('accountType', e.target.value)}
                    className={masterSelect}
                    required
                  >
                    <option value="">Select Account Type</option>
                    <option value="Savings">Savings</option>
                    <option value="Current">Current</option>
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Branch Name</label>
                  <input
                    type="text"
                    value={formData.branchName}
                    onChange={(e) => handleChange('branchName', e.target.value)}
                    className={masterInput}
                    required
                  />
                </div>
              </div>

              <div className={masterFormGrid3}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className={masterFormActions}>
                {editId && (
                  <button type="button" onClick={resetForm} className={masterCancelButton}>
                    Cancel
                  </button>
                )}
                <button type="submit" className={masterAccentButton}>
                  {editId ? 'Update Details' : 'Save Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className={masterPanel}>
          <div className={masterTableWrap}>
            <table className={masterTable}>
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={masterTableHeaderCell}>Bank Name</th>
                  <th className={masterTableHeaderCell}>Account Number</th>
                  <th className={masterTableHeaderCell}>IFSC Code</th>
                  <th className={masterTableHeaderCell}>Holder Name</th>
                  <th className={masterTableHeaderCell}>Account Type</th>
                  <th className={masterTableHeaderCell}>Branch Name</th>
                  <th className={masterTableHeaderCellCenter}>Status</th>
                  <th className={masterTableHeaderCellCenter}>Action</th>
                </tr>
              </thead>
              <tbody>
                {bankingDetails.map((details) => (
                  <tr key={details._id} className={masterTableRow}>
                    <td className={masterTableData}>{details.bankName || 'N/A'}</td>
                    <td className={masterTableData}>{details.accountNumber || 'N/A'}</td>
                    <td className={masterTableData}>{details.ifscCode || 'N/A'}</td>
                    <td className={masterTableData}>{details.holderName || 'N/A'}</td>
                    <td className={masterTableData}>{details.accountType || 'N/A'}</td>
                    <td className={masterTableData}>{details.branchName || 'N/A'}</td>
                    <td className={masterTableStatusCell}>
                      <span className={details.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                        {details.status}
                      </span>
                    </td>
                    <td className={masterTableActionCell}>
                      <div className="flex justify-center gap-1">
                        <button onClick={() => handleEdit(details)} className={masterEditButton}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(details._id)} className="px-2 py-0.5 bg-red-500 text-white rounded text-[10px] font-bold uppercase">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyBankingDetails;